import { useEffect, useState } from "react";
import { downloadFileBlob } from "../lib/api";
import { Dialog, DialogContent } from "./ui/dialog";
import { Paperclip, FilePdf, MagnifyingGlass, Spinner } from "@phosphor-icons/react";

/**
 * Inline receipt preview with click-to-expand.
 *
 * Props:
 *  - receiptPath: storage path (or null/empty if no receipt)
 *  - size: "sm" | "md" — thumbnail size (sm=56px, md=96px)
 *
 * Shows nothing if `receiptPath` is empty. Otherwise renders a thumbnail
 * (or a PDF icon for .pdf files) and opens a full-size modal on click.
 */
export default function ReceiptPreview({ receiptPath, size = "md" }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  const isPdf = (receiptPath || "").toLowerCase().endsWith(".pdf");
  const dim = size === "sm" ? 56 : 96;

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setBlobUrl(null);
    setError(false);
    if (!receiptPath) return;
    setLoading(true);
    downloadFileBlob(receiptPath)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setBlobUrl(url);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(true);
          console.warn("Receipt preview load failed:", e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [receiptPath]);

  if (!receiptPath) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => blobUrl && setOpen(true)}
        disabled={!blobUrl}
        data-testid="receipt-preview-thumb"
        className="block rounded-sm border border-zinc-200 overflow-hidden flex-shrink-0 hover:border-[#002FA7] hover:shadow-md transition-all duration-200 cursor-pointer relative group disabled:cursor-default"
        style={{ width: dim, height: dim }}
        title="Click to view full receipt"
      >
        {loading ? (
          <div className="w-full h-full flex items-center justify-center bg-zinc-50">
            <Spinner size={20} className="animate-spin text-zinc-400" />
          </div>
        ) : error ? (
          <div className="w-full h-full flex items-center justify-center bg-rose-50">
            <span className="text-[9px] uppercase text-rose-500 font-semibold">Err</span>
          </div>
        ) : isPdf ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-rose-50 gap-0.5">
            <FilePdf size={dim * 0.36} weight="duotone" className="text-rose-600" />
            <span className="text-[9px] uppercase font-bold text-rose-700 tracking-wider">PDF</span>
          </div>
        ) : (
          <img src={blobUrl} alt="Receipt" className="w-full h-full object-cover" />
        )}
        {blobUrl && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <MagnifyingGlass size={dim * 0.22} weight="bold" className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-2 rounded-sm" data-testid="receipt-preview-modal">
          {blobUrl &&
            (isPdf ? (
              <iframe src={blobUrl} title="Receipt PDF preview" className="w-full h-[85vh] rounded-sm border-0" />
            ) : (
              <img src={blobUrl} alt="Receipt full preview" className="w-full h-auto max-h-[85vh] object-contain rounded-sm" />
            ))}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Compact inline indicator for list rows — a paperclip badge if the expense
 * has a receipt, nothing otherwise. Used in the Expenses / Approvals list
 * rows alongside the vendor name.
 */
export function ReceiptBadge({ receiptPath }) {
  if (!receiptPath) return null;
  return (
    <span
      data-testid="receipt-badge"
      title="Receipt attached"
      className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-emerald-50 text-emerald-600 flex-shrink-0"
    >
      <Paperclip size={10} weight="bold" />
    </span>
  );
}
